// useMultiSelect — 다중 선택 상태 관리 훅 (U44).
//
// zustand 없이 useState + useCallback으로 구현.
// 외부에서 "현재 보이는 항목 배열"(allIds)을 전달받아
// 범위 선택(selectRange)과 전체 선택(selectAll)을 처리.
//
// 선택 상태:
//   selected: Set<number>         — 현재 선택된 id 집합
//   lastSelectedId: number | null — Shift+클릭 범위 기준점
//
// 동작:
//   toggle(id)                    — 단일 항목 토글 (Cmd/Ctrl+클릭)
//   select(ids)                   — 특정 id 집합으로 교체
//   clear()                       — 전체 해제
//   selectAll(allIds)             — 전달된 ids 전체 선택
//   selectRange(from, to, all)    — from~to 범위 선택 (Shift+클릭)
//   handleItemClick(id, e, all)   — 클릭 이벤트 통합 핸들러

import { useState, useCallback, useRef } from 'react';

export interface UseMultiSelectReturn {
  /** 현재 선택된 id Set */
  selected: Set<number>;
  /** 선택 항목 수 */
  selectedCount: number;
  /** 단일 항목 토글 (Cmd/Ctrl+클릭) */
  toggle: (id: number) => void;
  /** 특정 id 집합으로 교체 */
  select: (ids: number[]) => void;
  /** 전체 해제 */
  clear: () => void;
  /** 전달된 ids 전체 선택 */
  selectAll: (allIds: number[]) => void;
  /**
   * from~to 범위 선택 (Shift+클릭).
   * allIds: 현재 화면에 보이는 항목 순서 배열.
   * from/to: 범위 양 끝 id.
   */
  selectRange: (from: number, to: number, allIds: number[]) => void;
  /**
   * 클릭 이벤트 통합 핸들러.
   * - 일반 클릭: 단일 선택 (선택 모드 진입 또는 기존 선택에서 단일로 축소)
   * - Shift+클릭: 범위 선택
   * - Cmd/Ctrl+클릭: 토글
   * allIds: 현재 화면에 보이는 항목 배열 (범위 선택에 필요).
   */
  handleItemClick: (id: number, e: React.MouseEvent, allIds: number[]) => void;
  /** 마지막 선택 항목 id (범위 선택 기준점) */
  lastSelectedId: number | null;
}

export function useMultiSelect(): UseMultiSelectReturn {
  const [selected, setSelected] = useState<Set<number>>(new Set());
  // useRef로 lastSelectedId 관리 — 렌더 트리거 없이 범위 선택 기준점만 추적
  const lastSelectedIdRef = useRef<number | null>(null);
  // lastSelectedId를 외부에서 읽을 수 있도록 state로도 관리
  const [lastSelectedId, setLastSelectedId] = useState<number | null>(null);

  const updateLastSelected = useCallback((id: number | null) => {
    lastSelectedIdRef.current = id;
    setLastSelectedId(id);
  }, []);

  const toggle = useCallback((id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
    updateLastSelected(id);
  }, [updateLastSelected]);

  const select = useCallback((ids: number[]) => {
    setSelected(new Set(ids));
    if (ids.length > 0) {
      updateLastSelected(ids[ids.length - 1]);
    }
  }, [updateLastSelected]);

  const clear = useCallback(() => {
    setSelected(new Set());
    updateLastSelected(null);
  }, [updateLastSelected]);

  const selectAll = useCallback((allIds: number[]) => {
    setSelected(new Set(allIds));
    if (allIds.length > 0) {
      updateLastSelected(allIds[allIds.length - 1]);
    }
  }, [updateLastSelected]);

  const selectRange = useCallback((from: number, to: number, allIds: number[]) => {
    const fromIdx = allIds.indexOf(from);
    const toIdx = allIds.indexOf(to);

    if (fromIdx === -1 || toIdx === -1) {
      // from/to 중 하나가 없으면 단일 선택
      setSelected(new Set([to]));
      updateLastSelected(to);
      return;
    }

    const start = Math.min(fromIdx, toIdx);
    const end = Math.max(fromIdx, toIdx);
    const rangeIds = allIds.slice(start, end + 1);

    setSelected(new Set(rangeIds));
    updateLastSelected(to);
  }, [updateLastSelected]);

  const handleItemClick = useCallback(
    (id: number, e: React.MouseEvent, allIds: number[]) => {
      const isCmd = e.metaKey || e.ctrlKey;
      const isShift = e.shiftKey;

      if (isCmd) {
        // Cmd/Ctrl+클릭: 토글
        toggle(id);
      } else if (isShift && lastSelectedIdRef.current !== null) {
        // Shift+클릭: 범위 선택
        selectRange(lastSelectedIdRef.current, id, allIds);
      } else if (isShift) {
        // Shift+클릭이지만 기준점 없음 — 단일 선택
        setSelected(new Set([id]));
        updateLastSelected(id);
      } else {
        // 일반 클릭: 단일 선택 (이미 선택된 단 1개이면 해제 — 토글처럼 동작)
        setSelected((prev) => {
          if (prev.size === 1 && prev.has(id)) {
            // 이미 단일 선택 상태에서 같은 카드 클릭 → 해제
            updateLastSelected(null);
            return new Set();
          }
          updateLastSelected(id);
          return new Set([id]);
        });
        // updateLastSelected는 setSelected 내부에서 호출되지만
        // 상태 업데이트 비동기성으로 인해 prev 기반 조건 외부에서도 호출
        // (단, 이미 내부에서 호출했으므로 여기서는 건너뜀)
      }
    },
    [toggle, selectRange, updateLastSelected]
  );

  return {
    selected,
    selectedCount: selected.size,
    toggle,
    select,
    clear,
    selectAll,
    selectRange,
    handleItemClick,
    lastSelectedId,
  };
}
